import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Card, Button, Space, Tag, Modal, Badge, List, Typography } from 'antd';
import { PlusOutlined, CalendarOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { Meal } from '../../../../types/meal.types';
import { all_routes } from '../../../../router/all_routes';
import { useMeals, useMealActions } from '../../../../hooks/useMeals';
import CreateMealModal from './CreateMealModal';
import UpdateMealModal from './UpdateMealModal';
import dayjs, { Dayjs } from 'dayjs';

const { Text } = Typography;

const MealCalendar: React.FC = () => {
  const routes = all_routes;

  const {
    meals,
    fetchMeals
  } = useMeals();

  const { deleteMeal, updateMealStatus } = useMealActions();

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [updateModalVisible, setUpdateModalVisible] = useState(false);
  const [selectedMeal, setSelectedMeal] = useState<Meal | null>(null);
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [dayMealsModalVisible, setDayMealsModalVisible] = useState(false);
  const [selectedDayMeals, setSelectedDayMeals] = useState<Meal[]>([]);

  useEffect(() => {
    // Bu ayın yemeklerini getir
    const startOfMonth = dayjs().startOf('month');
    const endOfMonth = dayjs().endOf('month');

    fetchMeals({
      startDate: startOfMonth.format('YYYY-MM-DD'),
      endDate: endOfMonth.format('YYYY-MM-DD')
    });
  }, [fetchMeals]);

  const getMealsForDate = (date: Dayjs): Meal[] => {
    const dateStr = date.format('YYYY-MM-DD');
    return meals.filter(meal => meal.date === dateStr);
  };

  const dateCellRender = (date: Dayjs) => {
    const dayMeals = getMealsForDate(date);

    if (dayMeals.length === 0) return null;

    return (
      <div style={{ fontSize: '12px' }}>
        {dayMeals.slice(0, 2).map(meal => (
          <div key={meal.id} style={{ marginBottom: '2px' }}>
            <Badge
              status={meal.isActive ? 'success' : 'default'}
              text={
                <span style={{ fontSize: '11px' }}>
                  {meal.title.length > 10 ? `${meal.title.substring(0, 10)}...` : meal.title}
                </span>
              }
            />
          </div>
        ))}
        {dayMeals.length > 2 && (
          <div style={{ fontSize: '10px', color: '#666' }}>
            +{dayMeals.length - 2} daha
          </div>
        )}
      </div>
    );
  };

  const onDateSelect = (date: Dayjs) => {
    const dayMeals = getMealsForDate(date);
    setSelectedDate(date);
    setSelectedDayMeals(dayMeals);
    setDayMealsModalVisible(true);
  };

  const onPanelChange = (date: Dayjs) => {
    // Ay değiştiğinde o ayın yemeklerini getir
    const startOfMonth = date.startOf('month');
    const endOfMonth = date.endOf('month');

    fetchMeals({
      startDate: startOfMonth.format('YYYY-MM-DD'),
      endDate: endOfMonth.format('YYYY-MM-DD')
    });
  };

  const handleEdit = (meal: Meal) => {
    setSelectedMeal(meal);
    setUpdateModalVisible(true);
    setDayMealsModalVisible(false);
  };

  const handleDelete = async (meal: Meal) => {
    const success = await deleteMeal(meal.id);
    if (success) {
      // Mevcut aya ait yemekleri yeniden getir
      const startOfMonth = selectedDate.startOf('month');
      const endOfMonth = selectedDate.endOf('month');

      fetchMeals({
        startDate: startOfMonth.format('YYYY-MM-DD'),
        endDate: endOfMonth.format('YYYY-MM-DD')
      });

      // Modal'daki listeyi güncelle
      const updatedDayMeals = selectedDayMeals.filter(m => m.id !== meal.id);
      setSelectedDayMeals(updatedDayMeals);
    }
  };

  const handleStatusToggle = async (meal: Meal) => {
    const success = await updateMealStatus(meal.id, !meal.isActive);
    if (success) {
      // Local state'i güncelle
      const updatedDayMeals = selectedDayMeals.map(m =>
        m.id === meal.id ? { ...m, isActive: !m.isActive } : m
      );
      setSelectedDayMeals(updatedDayMeals);

      // Ana listeyi yeniden getir
      const startOfMonth = selectedDate.startOf('month');
      const endOfMonth = selectedDate.endOf('month');

      fetchMeals({
        startDate: startOfMonth.format('YYYY-MM-DD'),
        endDate: endOfMonth.format('YYYY-MM-DD')
      });
    }
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'default';
  };

  const getStatusText = (isActive: boolean) => {
    return isActive ? 'Aktif' : 'Pasif';
  };



  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Page Header */}
        <div className="page-header">
          <div className="row">
            <div className="col-sm-12">
              <ul className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.meals}>Yemek Yönetimi</Link>
                </li>
                <li className="breadcrumb-item active">Yemek Takvimi</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="row">
          <div className="col-12">
            <Card
              title={
                <Space>
                  <CalendarOutlined />
                  Yemek Takvimi
                </Space>
              }
              extra={
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setCreateModalVisible(true)}
                >
                  Yeni Yemek Ekle
                </Button>
              }
            >
              <Calendar
                dateCellRender={dateCellRender}
                onSelect={onDateSelect}
                onPanelChange={onPanelChange}
                style={{ minHeight: '600px' }}
              />
            </Card>
          </div>
        </div>

        {/* Day Meals Modal */}
        <Modal
          title={
            <Space>
              <CalendarOutlined />
              {selectedDate.format('DD MMMM YYYY')} - Günün Yemekleri
            </Space>
          }
          open={dayMealsModalVisible}
          onCancel={() => setDayMealsModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setDayMealsModalVisible(false)}>
              Kapat
            </Button>,
            <Button
              key="add"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setDayMealsModalVisible(false);
                setCreateModalVisible(true);
              }}
            >
              Yemek Ekle
            </Button>
          ]}
          width={700}
        >
          {selectedDayMeals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Text type="secondary">Bu tarihte henüz yemek planlanmamış.</Text>
            </div>
          ) : (
            <List
              itemLayout="horizontal"
              dataSource={selectedDayMeals}
              renderItem={(meal) => (
                <List.Item
                  actions={[
                    <Button
                      type="link"
                      size="small"
                      onClick={() => handleStatusToggle(meal)}
                    >
                      {(meal.isActive ?? true) ? 'Pasif Yap' : 'Aktif Yap'}
                    </Button>,
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => handleEdit(meal)}
                    >
                      Düzenle
                    </Button>,
                    <Button
                      type="link"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleDelete(meal)}
                    >
                      Sil
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        {meal.title}
                        <Tag color={getStatusColor(meal.isActive ?? true)}>
                          {getStatusText(meal.isActive ?? true)}
                        </Tag>
                      </Space>
                    }
                    description={meal.description}
                  />
                </List.Item>
              )}
            />
          )}
        </Modal>

        {/* Create/Update Modals */}
        <CreateMealModal
          visible={createModalVisible}
          onCancel={() => setCreateModalVisible(false)}
          onSuccess={() => {
            setCreateModalVisible(false);
            // Mevcut aya ait yemekleri yeniden getir
            const startOfMonth = selectedDate.startOf('month');
            const endOfMonth = selectedDate.endOf('month');

            fetchMeals({
              startDate: startOfMonth.format('YYYY-MM-DD'),
              endDate: endOfMonth.format('YYYY-MM-DD')
            });
          }}
        />

        <UpdateMealModal
          visible={updateModalVisible}
          meal={selectedMeal}
          onCancel={() => {
            setUpdateModalVisible(false);
            setSelectedMeal(null);
          }}
          onSuccess={() => {
            setUpdateModalVisible(false);
            setSelectedMeal(null);
            // Mevcut aya ait yemekleri yeniden getir
            const startOfMonth = selectedDate.startOf('month');
            const endOfMonth = selectedDate.endOf('month');

            fetchMeals({
              startDate: startOfMonth.format('YYYY-MM-DD'),
              endDate: endOfMonth.format('YYYY-MM-DD')
            });
          }}
        />
      </div>
    </div>
  );
};

export default MealCalendar;